import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/cn";

type Slot = { start_at: string; staff_id: string; score: number; factors: Record<string, number> };
type Staff = { id: string; full_name: string; photo_asset_id?: string | null };

type CatalogAsset = { url: string; variants?: Record<string, string>; alt_text?: string | null };
type StaffSelectorStyle = { variant?: "pills" | "cardsWithPhotos"; showPhotos?: boolean };
type SlotChipStyle = { variant?: "outline" | "filled"; size?: "sm" | "md" };

type WaitlistPayload = {
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  notes?: string;
};

type WaitlistState = {
  isSubmitting: boolean;
  isSubmitted: boolean;
  error?: string;
  initialName?: string;
  initialPhone?: string;
  initialEmail?: string;
  phoneLocked?: boolean;
  onSubmit: (payload: WaitlistPayload) => void;
};

type Props = {
  slots: Slot[];
  staff: Staff[];
  assets: Record<string, CatalogAsset>;
  staffSelectorStyle?: StaffSelectorStyle;
  slotChipStyle?: SlotChipStyle;
  selectedStaffId: string;
  onStaffChange: (id: string) => void;
  day: string;
  onDayChange: (d: string) => void;
  selectedSlot: string;
  onSlotSelect: (start_at: string, staff_id: string) => void;
  onContinue: () => void;
  isLoading: boolean;
  serviceName: string;
  staffNameMap: Map<string, string>;
  locale: string;
  waitlist?: WaitlistState;
};

function buildDateStrip(locale: string, todayLabel: string): { iso: string; label: string; weekday: string }[] {
  const items: { iso: string; label: string; weekday: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 14; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() + i);
    const iso = d.toISOString().slice(0, 10);
    const weekday = d.toLocaleDateString(locale, { weekday: "short" });
    const label = i === 0 ? todayLabel : d.toLocaleDateString(locale, { month: "short", day: "numeric" });
    items.push({ iso, label, weekday });
  }
  return items;
}

type SlotGroup = { label: string; items: Slot[] };

function groupSlots(slots: Slot[]): SlotGroup[] {
  const morning: Slot[] = [];
  const afternoon: Slot[] = [];
  const evening: Slot[] = [];

  for (const s of slots) {
    const h = new Date(s.start_at).getHours();
    if (h < 12) morning.push(s);
    else if (h < 17) afternoon.push(s);
    else evening.push(s);
  }

  const groups: SlotGroup[] = [];
  if (morning.length) groups.push({ label: "Morning", items: morning });
  if (afternoon.length) groups.push({ label: "Afternoon", items: afternoon });
  if (evening.length) groups.push({ label: "Evening", items: evening });
  return groups;
}

export function StepTimeSlots({
  slots,
  staff,
  assets,
  staffSelectorStyle,
  slotChipStyle,
  selectedStaffId,
  onStaffChange,
  day,
  onDayChange,
  selectedSlot,
  onSlotSelect,
  onContinue,
  isLoading,
  serviceName,
  staffNameMap,
  locale,
  waitlist,
}: Props) {
  const { t } = useTranslation();
  const tr = (key: string, fallback: string, options?: Record<string, unknown>) => {
    const translated = t(key, { ...options, defaultValue: fallback });
    return translated === key ? fallback : translated;
  };

  const todayLabel = tr("client.booking.today", "Today");
  const dates = useMemo(() => buildDateStrip(locale, todayLabel), [locale, todayLabel]);
  const groups = useMemo(() => groupSlots(slots), [slots]);
  const maxScore = useMemo(() => {
    if (!slots.length) return 0;
    return Math.max(...slots.map((s) => s.score));
  }, [slots]);

  const [waitlistName, setWaitlistName] = useState(waitlist?.initialName || "");
  const [waitlistPhone, setWaitlistPhone] = useState(waitlist?.initialPhone || "");
  const [waitlistEmail, setWaitlistEmail] = useState(waitlist?.initialEmail || "");
  const [waitlistNotes, setWaitlistNotes] = useState("");

  useEffect(() => {
    if (!waitlist) return;
    if (!waitlistName && waitlist.initialName) setWaitlistName(waitlist.initialName);
    if (!waitlistPhone && waitlist.initialPhone) setWaitlistPhone(waitlist.initialPhone);
    if (!waitlistEmail && waitlist.initialEmail) setWaitlistEmail(waitlist.initialEmail);
  }, [
    waitlist,
    waitlistName,
    waitlistPhone,
    waitlistEmail,
    waitlist?.initialName,
    waitlist?.initialPhone,
    waitlist?.initialEmail,
  ]);

  const selectedTime = selectedSlot
    ? new Date(selectedSlot).toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" })
    : "";
  const selectedStaffName = selectedStaffId ? staffNameMap.get(selectedStaffId) || "" : "";
  const staffVariant = staffSelectorStyle?.variant || "pills";
  const showPhotos = staffSelectorStyle?.showPhotos ?? false;
  const slotVariant = slotChipStyle?.variant || "outline";
  const slotSize = slotChipStyle?.size || "md";
  const slotSizeClass = slotSize === "sm" ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm";
  const selectedSlotData = slots.find((s) => s.start_at === selectedSlot);
  const topSlot = slots.length ? [...slots].sort((a, b) => b.score - a.score)[0] : undefined;
  const explanationSlot = selectedSlotData || topSlot;
  const factorNames: Record<string, string> = {
    repeat_client: tr("client.booking.factor.repeatClient", "Repeat client fit"),
    gap_fill: tr("client.booking.factor.gapFill", "Gap fill optimization"),
    load_balance: tr("client.booking.factor.loadBalance", "Load balance"),
    time_of_day: tr("client.booking.factor.timeOfDay", "Time of day"),
  };

  const canSubmitWaitlist = Boolean(waitlistName.trim() || waitlist?.phoneLocked);

  return (
    <div className="step-enter">
      <h2 className="mb-4 text-lg font-semibold text-slate-800">{tr("client.booking.step.time.title", "Pick date & time")}</h2>

      {staffVariant === "cardsWithPhotos" ? (
        <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <button
            onClick={() => onStaffChange("")}
            className={cn(
              "rounded-xl border p-3 text-left transition-colors min-h-[44px]",
              !selectedStaffId ? "border-teal-600 bg-teal-50" : "border-slate-200 bg-white hover:border-slate-300"
            )}
          >
            <p className="font-medium text-sm">{tr("client.booking.anySpecialist", "Any specialist")}</p>
          </button>
          {staff.slice(0, 8).map((s) => {
            const asset = s.photo_asset_id ? assets[s.photo_asset_id] : undefined;
            return (
              <button
                key={s.id}
                onClick={() => onStaffChange(s.id)}
                data-testid={`staff-card-${s.id}`}
                className={cn(
                  "rounded-xl border p-3 text-left transition-colors min-h-[44px]",
                  selectedStaffId === s.id ? "border-teal-600 bg-teal-50" : "border-slate-200 bg-white hover:border-slate-300"
                )}
              >
                {showPhotos ? (
                  <div className="mb-2 h-20 overflow-hidden rounded-lg border bg-slate-100">
                    {asset?.url ? (
                      <img
                        src={asset.variants?.card_4x3 || asset.variants?.card_1x1 || asset.variants?.web_1600 || asset.url}
                        alt={asset.alt_text || s.full_name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full bg-slate-100" />
                    )}
                  </div>
                ) : null}
                <p className="font-medium text-sm">{s.full_name}</p>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="mb-4 flex flex-wrap gap-2">
          <button
            onClick={() => onStaffChange("")}
            className={cn(
              "min-h-[44px] rounded-full px-4 py-1.5 text-sm font-medium border transition-colors",
              !selectedStaffId
                ? "bg-teal-600 text-white border-teal-600"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            )}
          >
            {tr("client.booking.anySpecialist", "Any specialist")}
          </button>
          {staff.slice(0, 6).map((s) => (
            <button
              key={s.id}
              onClick={() => onStaffChange(s.id)}
              className={cn(
                "min-h-[44px] rounded-full px-4 py-1.5 text-sm font-medium border transition-colors",
                selectedStaffId === s.id
                  ? "bg-teal-600 text-white border-teal-600"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
              )}
            >
              {s.full_name}
            </button>
          ))}
        </div>
      )}

      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-2 scrollbar-hide">
        {dates.map((d) => (
          <button
            key={d.iso}
            onClick={() => onDayChange(d.iso)}
            className={cn(
              "flex-shrink-0 min-w-[72px] flex flex-col items-center rounded-xl px-3 py-2 text-sm border transition-colors min-h-[44px]",
              day === d.iso
                ? "bg-teal-600 text-white border-teal-600"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            )}
          >
            <span className="text-xs font-medium">{d.weekday}</span>
            <span className="font-bold">{d.label}</span>
          </button>
        ))}
      </div>

      <div className="mt-4">
        {isLoading ? (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="skeleton h-10 rounded-full" />
            ))}
          </div>
        ) : null}

        {!isLoading && slots.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-base font-semibold text-slate-700">{tr("client.booking.noSlots", "No available times")}</p>
            <p className="mt-1 text-sm text-slate-500">{tr("client.booking.noSlotsHint", "Try another date or join the waitlist.")}</p>

            {waitlist ? (
              <div className="mt-3">
                {waitlist.isSubmitted ? (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                    <p className="font-semibold">{tr("client.waitlist.successTitle", "You are on the waitlist")}</p>
                    <p className="mt-1">{tr("client.waitlist.successHint", "We will contact you when a matching slot opens.")}</p>
                  </div>
                ) : (
                  <div className="grid gap-2">
                    <input
                      className="input"
                      placeholder={tr("client.waitlist.name", "Your name")}
                      value={waitlistName}
                      onChange={(event) => setWaitlistName(event.target.value)}
                    />
                    <input
                      className="input"
                      placeholder={tr("client.waitlist.phone", "Phone")}
                      value={waitlistPhone}
                      onChange={(event) => setWaitlistPhone(event.target.value)}
                      disabled={waitlist.phoneLocked}
                    />
                    <input
                      className="input"
                      placeholder={tr("client.waitlist.email", "Email (optional)")}
                      value={waitlistEmail}
                      onChange={(event) => setWaitlistEmail(event.target.value)}
                    />
                    <textarea
                      className="input min-h-[92px]"
                      placeholder={tr("client.waitlist.notes", "Notes (optional)")}
                      value={waitlistNotes}
                      onChange={(event) => setWaitlistNotes(event.target.value)}
                    />
                    <button
                      className="btn w-full sm:w-auto"
                      type="button"
                      disabled={waitlist.isSubmitting || !canSubmitWaitlist}
                      onClick={() =>
                        waitlist.onSubmit({
                          customer_name: waitlistName.trim() || undefined,
                          customer_phone: waitlistPhone.trim() || undefined,
                          customer_email: waitlistEmail.trim() || undefined,
                          notes: waitlistNotes.trim() || undefined,
                        })
                      }
                    >
                      {waitlist.isSubmitting
                        ? tr("client.waitlist.submitting", "Saving...")
                        : tr("client.waitlist.join", "Join waitlist")}
                    </button>
                    {waitlist.error ? <p className="text-sm text-red-600">{waitlist.error}</p> : null}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        ) : null}

        {!isLoading &&
          groups.map((group) => (
            <div key={group.label} className="mb-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{tr(`client.booking.group.${group.label.toLowerCase()}`, group.label)}</p>
              <div className="flex flex-wrap gap-2">
                {group.items.map((s) => {
                  const time = new Date(s.start_at).toLocaleTimeString(locale, {
                    hour: "numeric",
                    minute: "2-digit",
                  });
                  const isSelected = selectedSlot === s.start_at;
                  const isTop = maxScore > 0 && s.score === maxScore;

                  return (
                    <button
                      key={`${s.staff_id}-${s.start_at}`}
                      onClick={() => onSlotSelect(s.start_at, s.staff_id)}
                      data-testid={`slot-${s.staff_id}-${s.start_at}`}
                      className={cn(
                        "slot-pill relative rounded-full font-medium border transition-all min-h-[44px]",
                        slotSizeClass,
                        isSelected
                          ? "slot-pill-selected"
                          : slotVariant === "filled"
                            ? "bg-slate-100 text-slate-700 border-transparent"
                            : "bg-white text-slate-700 border-slate-200"
                      )}
                    >
                      <span>{time}</span>
                      {isTop && !isSelected ? (
                        <span className="absolute -right-1 -top-2 flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700">
                          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        </span>
                      ) : null}
                      {!selectedStaffId && !isSelected ? (
                        <span className="mt-0.5 block text-xs text-slate-400">
                          {staffNameMap.get(s.staff_id) || tr("client.booking.defaultStaff", "Staff")}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
      </div>

      {explanationSlot ? (
        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs uppercase tracking-wide text-slate-500">{tr("client.booking.whyRecommended", "Why recommended")}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {Object.entries(explanationSlot.factors || {}).map(([key, value]) => (
              <span key={key} className="factor-chip">
                {factorNames[key] || key}: {Number(value).toFixed(1)}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {selectedSlot ? (
        <button onClick={onContinue} className="btn mt-4 w-full sm:w-auto">
          {tr("client.booking.continueWithSlot", "Continue with {{service}} at {{time}}", {
            service: serviceName,
            time: selectedTime,
          })}
          {selectedStaffName ? ` ${tr("client.booking.withStaff", "with")} ${selectedStaffName}` : ""}
        </button>
      ) : null}
    </div>
  );
}
